import { apiClient } from '../core/api/apiClient';
import type { AlumniProfile } from './alumni.service';

export interface SummaryStats {
  total_alumni: number;
  total_projects: number;
  total_proposals: number;
  total_donors: number;
  total_with_vals: number;
  vals_min_batch?: string;
  vals_max_batch?: string;
  untapped_fundraising_potential?: number;
  wealth_utilization_percentage?: number;

}

export interface Proposal {
  _id: string;
  title: string;
  domain: string;
  budget_lakhs?: number;
  created_at: string;
}

export interface Project {
  _id: string;
  project_title: string;
  project_id: string;
  budget: string;
  created_at: string;
}

export interface Activity {
  _id: string;
  email: string;
  action: string;
  timestamp: string;
  details?: any;
}

export interface RecentData {
  proposals: Proposal[];
  projects: Project[];
  alumni: AlumniProfile[];
  activities: Activity[];
}

export interface SearchResults {
  alumni: AlumniProfile[];
  projects: Project[];
  proposals: Proposal[];
}

export interface MultiplierAlumni {
  _id: string;
  donor_id: string;
  name: string;
  company?: string;
  recommended_ask: number;
  cumulative_giving: number;
  avatar?: string;
}

export interface MultiplierBucket {
  multiplier: number;
  label: string;
  count: number;
  alumni: MultiplierAlumni[];
}

export interface OpportunityFilters {
  companies: string[];
  batches: string[];
  locations: string[];
}

export interface OpportunityData {
  donor_id: string;
  name: string;
  company?: string;
  designation?: string;
  batch?: string;
  work_location?: string;
  recommended_ask: number;
  cumulative_giving: number;
  confidence: number;
  opportunity_score: number;
}

export type BucketBy = 'sector' | 'target_ask' | 'location' | 'territory' | 'designation';

export type PlaceKind = 'city' | 'state' | 'country';

/** A selectable place on the Location view, with how many chart alumni live in it. */
export interface ConstellationPlace {
  /** city:<GeoNames id> · state:<CC>.<admin1> · country:<ISO-2> */
  id: string;
  kind: PlaceKind;
  name: string;
  /** "Tamil Nadu, India" for a city, "India" for a state, "" for a country. */
  region: string;
  count: number;
}

export interface ConstellationPlaces {
  places: ConstellationPlace[];
  default_place_id: string;
  min_radius_km: number;
  max_radius_km: number;
  default_radius_km: number;
}

export interface TerritoryPlace {
  id: string;
  label: string;
}

export interface Territory {
  id: string;
  label: string;
  owner: string;
  places: TerritoryPlace[];
}

/**
 * One plotted dot, and nothing more. Everything needed to place it, colour it
 * and navigate from it — the descriptive fields the tooltip shows live in
 * ConstellationDetail and are fetched on hover, because carrying them for all
 * ~25k rows was ~45% of a 7.4 MB payload to render at most one of them.
 */
export interface BucketedAlumni {
  donor_id: string;
  /** Midpoint of parsed_recommended_ask — not the low bound. */
  recommended_ask: number;
  opportunity_score: number;
  /**
   * Sum of monday_data.amount. The one descriptive field carried for every
   * row: the backend already computes it for the opportunity score, and the
   * alumni list sorts on it and on the ask-minus-giving gap, which it cannot
   * do from values fetched only for the rows currently on screen.
   */
  cumulative_giving: number;
  bucket: string;
  /**
   * Straight-line km from the selected centre. Only present on the Location
   * view when the centre is a city or a map point and this alumnus has
   * coordinates; it is what the chart's colour gradient reads.
   */
  distance_km?: number;
}

/** Tooltip-only fields, fetched per donor_id on hover. */
export interface ConstellationDetail {
  donor_id: string;
  name: string;
  company?: string;
  designation?: string;
  /** VALS's current role: what the Designation view's groups are based on. */
  role?: string;
  /** VALS's current company, paired with `role`. */
  role_company?: string;
  department?: string;
  batch?: string | number;
  work_location?: string;
  cumulative_giving: number;
  confidence: number;
}

export interface BucketRange {
  label: string;
  min: number;
  /** null on the final, open-ended bucket. */
  max: number | null;
}

export interface BucketMeta {
  type: BucketBy;
  k: number;
  total_alumni: number;
  computed_at: string;
  /** Location view only: the centre the groups are measured from. */
  place?: { id: string; kind: PlaceKind | 'point'; name: string; region?: string; lat?: number | null; lng?: number | null };
  /** Location view only; null when the place is a state or country. */
  radius_km?: number | null;
  /**
   * Location view only: who the selection leaves off the chart — located
   * somewhere else, or with no location known at all. Only the people the
   * selection covers are plotted.
   */
  excluded?: { outside: number; no_location: number };
  /**
   * Location view only: total target ask across everyone the Directory filters
   * match, including the people left off the chart. The sidebar's ₹ shares
   * divide by this, so they mean the same as under Sector or Target Ask.
   */
  total_ask?: number;
  /**
   * Designation view only: plotted alumni whose VALS result has no designation
   * category yet. They show under "Other / Unclassified" until their VALS is
   * refreshed.
   */
  pending?: number;
}

export interface BucketedConstellation {
  alumni: BucketedAlumni[];
  buckets: string[];
  /** Populated only when bucketing by target_ask. */
  bucket_ranges: BucketRange[] | null;
  bucket_meta: BucketMeta;
}

/**
 * Declared as a type alias rather than an interface: apiClient types `params`
 * as Record<string, string | number | boolean>, and only type aliases get an
 * implicit index signature. An interface here fails to assign.
 */
export type BucketedConstellationParams = {
  bucket_by: BucketBy;
  q?: string;
  batch?: string;
  department?: string;
  location?: string;
  industry?: string;
  /** Location view: a place id, or lat/lng for any point on a map. */
  place?: string;
  lat?: number;
  lng?: number;
  radius_km?: number;
  territory_id?: string;
};

export interface FeatureCorrelation {
  feature: string;
  correlation: number;
  p_value: number;
}

/**
 * A pending entry with who added it and where it came from, for the admin
 * review row. Older cached responses may lack this — see `pending` below.
 */
export interface PendingDetail {
  category: string;
  sub_category: string | null;
  added_by: string | null;
  added_at: string | null;
  from_project: string | null;
}

export interface CategoriesResponse {
  categories: Record<string, string[]>;
  pending: { category: string; sub_category: string | null }[];
  /** Same entries as `pending`, plus who added each one and its source project. */
  pending_detail?: PendingDetail[];
  order: string[];
}

export interface SubCategoryPayload {
  name: string;
  pending: boolean;
}

export interface CategoryPayload {
  name: string;
  order: number;
  pending: boolean;
  subcategories: SubCategoryPayload[];
}

export interface ReplaceCategoriesPayload {
  categories: CategoryPayload[];
}

/** A single rule the system has learned from a reviewer correction. Also consumed by the Learned Rules tab (Task 17). */
export interface LearnedRule {
  id: string;
  text: string;
  scope: string;
  diagnosis: string;
  occurrences: number;
  active: boolean;
  created_from?: { ai: string; correct: string };
  /**
   * Set once the rule has been replayed against known-correct past cases.
   * A rule with no status yet (an amend/reinforce, which never gates) or a
   * status other than "passed" cannot be `active: true`.
   */
  replay_status?: 'passed' | 'failed' | 'stalled';
  replay_broke?: { project_id: string; project_name: string; expected: string; got: string } | null;
  replay_checked_at?: string | null;
  created_at?: string;
  last_reinforced_at?: string;
  /** Present when the rule was deactivated to stay within the active-rule cap. */
  deactivated_reason?: string | null;
  /**
   * Present when the rule names a category/sub-category a reviewer just
   * invented — parked inactive until an admin approves that value. A rule
   * carrying this can never be activated: it names a value the AI cannot
   * yet choose.
   */
  waiting_on?: { category: string; sub_category: string | null } | null;
}

export interface PromptResponse {
  template: string;
  rendered: string;
  learned_rules: LearnedRule[];
  updated_at: string | null;
  updated_by: string | null;
  is_default: boolean;
  markers: string[];
}

export const dashboardService = {
  getSummaryStats: async (): Promise<SummaryStats> => {
    return apiClient<SummaryStats>('/dashboard/summary', {
      method: 'GET',
    });
  },

  getRecentSubmissions: async (): Promise<RecentData> => {
    return apiClient<RecentData>('/dashboard/recent', {
      method: 'GET',
    });
  },

  searchAll: async (query: string): Promise<SearchResults> => {
    return apiClient<SearchResults>('/dashboard/search', {
      method: 'GET',
      params: { q: query },
    });
  },

  getAlumniMultipliers: async (): Promise<{ data: MultiplierBucket[] }> => {
    return apiClient<{ data: MultiplierBucket[] }>('/dashboard/alumni-multipliers', {
      method: 'GET',
    });
  },

  getOpportunityMatrix: async (filters?: { company?: string; batch?: string; work_location?: string }): Promise<{ data: OpportunityData[] }> => {
    return apiClient<{ data: OpportunityData[] }>('/dashboard/opportunity-matrix', {
      method: 'GET',
      params: filters,
    });
  },

  getOpportunityMatrixFilters: async (): Promise<{ data: OpportunityFilters }> => {
    return apiClient<{ data: OpportunityFilters }>('/dashboard/opportunity-matrix/filters', {
      method: 'GET',
    });
  },

  getBucketedConstellation: async (
    params: BucketedConstellationParams,
    signal?: AbortSignal,
  ): Promise<{ data: BucketedConstellation }> => {
    return apiClient<{ data: BucketedConstellation }>('/dashboard/bucketed-constellation', {
      method: 'GET',
      params,
      signal,
    });
  },

  /** Places for the Location view's picker, most-populated first. Cached server-side. */
  getConstellationPlaces: async (signal?: AbortSignal): Promise<{ data: ConstellationPlaces }> => {
    return apiClient<{ data: ConstellationPlaces }>('/dashboard/bucketed-constellation/places', {
      method: 'GET',
      signal,
    });
  },

  // Territories CRUD
  getTerritories: async (): Promise<{ data: Territory[] }> => {
    return apiClient<{ data: Territory[] }>('/dashboard/territories', { method: 'GET' });
  },
  createTerritory: async (body: { label: string; owner: string; places?: TerritoryPlace[] }): Promise<{ data: Territory }> => {
    return apiClient<{ data: Territory }>('/dashboard/territories', { method: 'POST', body });
  },
  updateTerritory: async (id: string, body: Partial<Territory>): Promise<void> => {
    return apiClient<void>(`/dashboard/territories/${id}`, { method: 'PUT', body });
  },
  deleteTerritory: async (id: string): Promise<void> => {
    return apiClient<void>(`/dashboard/territories/${id}`, { method: 'DELETE' });
  },
  addTerritoryPlace: async (id: string, place: TerritoryPlace): Promise<void> => {
    return apiClient<void>(`/dashboard/territories/${id}/places`, { method: 'POST', body: place });
  },
  removeTerritoryPlace: async (id: string, placeId: string): Promise<void> => {
    return apiClient<void>(`/dashboard/territories/${id}/places/${encodeURIComponent(placeId)}`, { method: 'DELETE' });
  },

  /**
   * Tooltip fields for the dots currently under the cursor. The backend caps
   * this at 50 ids per call, so callers must batch rather than ask for a page
   * of the chart at once.
   */
  getConstellationDetails: async (
    donorIds: string[],
    signal?: AbortSignal,
  ): Promise<{ data: ConstellationDetail[] }> => {
    return apiClient<{ data: ConstellationDetail[] }>(
      '/dashboard/bucketed-constellation/details',
      { method: 'GET', params: { donor_ids: donorIds.join(',') }, signal },
    );
  },

  getFeatureCorrelations: async (): Promise<{ data: FeatureCorrelation[] }> => {
    return apiClient<{ data: FeatureCorrelation[] }>('/dashboard/feature-correlations', {
      method: 'GET',
    });
  },

  getLiveInfo: async (): Promise<boolean> => {
    return apiClient<boolean>('/dashboard/live', {
      method: 'GET',
    });
  },

  getCategories: async (): Promise<CategoriesResponse> => {
    return apiClient<CategoriesResponse>('/dashboard/categories', { method: 'GET' });
  },

  /**
   * Admin only — replaces the entire category list. The backend rejects an
   * empty list, a blank name, or duplicate names with a 400 whose `detail`
   * is meant to be shown to the admin verbatim.
   */
  replaceCategories: async (payload: ReplaceCategoriesPayload): Promise<CategoriesResponse> => {
    return apiClient<CategoriesResponse>('/dashboard/admin/categories', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  getPrompt: async (): Promise<PromptResponse> => {
    return apiClient<PromptResponse>('/dashboard/admin/prompt', { method: 'GET' });
  },

  /**
   * Admin only — replaces the classification prompt template. The backend
   * rejects a template missing a required marker ({{ALLOWED_CATEGORIES}} or
   * {{LEARNED_RULES}}) with a 400 whose `detail` names the missing marker and
   * is meant to be shown to the admin verbatim.
   */
  savePrompt: async (template: string): Promise<PromptResponse> => {
    return apiClient<PromptResponse>('/dashboard/admin/prompt', {
      method: 'PUT',
      body: JSON.stringify({ template }),
    });
  },

  restorePrompt: async (): Promise<PromptResponse> => {
    return apiClient<PromptResponse>('/dashboard/admin/prompt/restore', { method: 'POST' });
  },

  /**
   * Adds a reviewer-supplied category or sub-category as pending: usable on
   * `project_id` immediately, withheld from the AI prompt until an admin
   * approves it. The backend rejects a near-duplicate with a 400 whose
   * `detail` names the existing value it probably restates.
   */
  addPendingCategory: async (payload: {
    category: string;
    sub_category?: string | null;
    project_id: string;
  }): Promise<CategoriesResponse> =>
    apiClient<CategoriesResponse>('/dashboard/categories/pending', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * Admin only — resolves one pending value: approve it as-is, rename and
   * approve, merge it away into an existing value, or reject it outright.
   */
  resolvePendingCategory: async (payload: {
    category: string; sub_category?: string | null;
    action: 'approve' | 'rename' | 'merge' | 'reject'; new_name?: string;
  }): Promise<CategoriesResponse> =>
    apiClient<CategoriesResponse>('/dashboard/admin/categories/pending/resolve', {
      method: 'POST', body: JSON.stringify(payload),
    }),

  /**
   * Admin only — edits a learned rule's text and/or flips it active/inactive.
   * The backend rejects activating a rule that carries `waiting_on` with a
   * 400: that rule names a category still pending approval, and a rule may
   * never reference a value the AI cannot choose.
   */
  patchRule: async (
    ruleId: string,
    payload: { text?: string; active?: boolean }
  ): Promise<PromptResponse> =>
    apiClient<PromptResponse>(`/dashboard/admin/prompt/rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Admin only — re-runs the replay check for one rule. The only way out of a
   * stalled check, and how a failed rule can be re-tried after a fix elsewhere.
   */
  recheckRule: async (ruleId: string): Promise<PromptResponse> =>
    apiClient<PromptResponse>(`/dashboard/admin/prompt/rules/${ruleId}/recheck`, {
      method: 'POST',
    }),

  /** Admin only — permanently removes a learned rule. */
  deleteRule: async (ruleId: string): Promise<PromptResponse> =>
    apiClient<PromptResponse>(`/dashboard/admin/prompt/rules/${ruleId}`, {
      method: 'DELETE',
    }),

  /** Admin only — scheduled AI research: settings, tonight's state, per-bucket counts. */
  getResearchSchedule: async (): Promise<{ data: ResearchScheduleOverview }> =>
    apiClient<{ data: ResearchScheduleOverview }>('/dashboard/admin/research-schedule', {
      method: 'GET',
    }),

  /**
   * Admin only — saves whichever parts are given. A bad bucket, frequency or
   * cap comes back as a 400 whose `detail` is meant to be shown verbatim.
   */
  updateResearchSchedule: async (
    payload: ResearchScheduleUpdate
  ): Promise<{ data: ResearchScheduleOverview }> =>
    apiClient<{ data: ResearchScheduleOverview }>('/dashboard/admin/research-schedule', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /** Admin only — the most recent scheduled runs, newest first. */
  getResearchScheduleRuns: async (limit = 20): Promise<{ data: ResearchScheduleRun[] }> =>
    apiClient<{ data: ResearchScheduleRun[] }>('/dashboard/admin/research-schedule/runs', {
      method: 'GET',
      params: { limit },
    }),

  /** One row per re-researched profile, from its latest rerun; filters apply to that rerun. */
  getResearchChanges: async (params: ResearchChangesQuery): Promise<ResearchChangesResponse> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach(item => searchParams.append(k, item));
      } else if (v !== undefined && v !== '') {
        searchParams.append(k, String(v));
      }
    });
    return apiClient<ResearchChangesResponse>(`/dashboard/research-changes?${searchParams.toString()}`, {
      method: 'GET',
    });
  },

  /** Every recorded version of a profile's AI research, newest first. */
  getResearchHistory: async (alumniId: string): Promise<ResearchHistoryResponse> =>
    apiClient<ResearchHistoryResponse>(`/dashboard/alumni/${alumniId}/research-history`, {
      method: 'GET',
    }),
};

// ── Scheduled AI research ───────────────────────────────────────────────────
// Times are UTC; ones read back from Mongo arrive without a zone suffix.

export type ResearchRunStatus = 'success' | 'failed' | 'skipped';

export interface ResearchScheduleBucket {
  category: string;
  enabled: boolean;
  frequency_days: number;
  updated_at: string | null;
  updated_by: string | null;
  /** Alumni VALS has put in this bucket. */
  profiles: number;
  /** Due right now; null while the bucket is off. */
  due: number | null;
  last_run_at: string | null;
  last_run_status: ResearchRunStatus | null;
}

export interface TargetAskScheduleBucket {
  label: string;
  min: number | null;
  max: number | null;
  enabled: boolean;
  frequency_days: number;
  updated_at: string | null;
  updated_by: string | null;
  profiles: number;
  due: number | null;
  last_run_at: string | null;
  last_run_status: ResearchRunStatus | null;
}

export interface ResearchScheduleOverview {
  paused: boolean;
  nightly_cap: number;
  window: { start_hour: number; end_hour: number; timezone: string };
  frequencies: number[];
  in_window: boolean;
  /** Nights to research every enabled alumnus once at the cap; null at cap 0. */
  cycle_nights: number | null;
  updated_at: string | null;
  updated_by: string | null;
  runner: {
    done_tonight: number;
    tripped: boolean;
    last_run_at: string | null;
    last_status: ResearchRunStatus | null;
    last_error: string | null;
    consecutive_failures: number;
  };
  buckets: ResearchScheduleBucket[];
  target_ask_buckets?: TargetAskScheduleBucket[];
}

export interface ResearchScheduleUpdate {
  paused?: boolean;
  nightly_cap?: number;
  bucket?: { category: string; enabled: boolean; frequency_days: number };
  target_ask_bucket?: { label: string; min: number | null; max: number | null; enabled: boolean; frequency_days: number };
}

export interface ResearchScheduleRun {
  profile_id: string;
  name: string | null;
  bucket: string;
  started_at: string;
  finished_at: string;
  status: ResearchRunStatus;
  error: string | null;
}
// ── AI research change history ──────────────────────────────────────────────
// Written by the backend's app/services/research_history.py on every save.

export type ResearchFieldGroup =
  | 'profile' | 'career' | 'earnings' | 'wealth' | 'network' | 'philanthropy' | 'scoring' | 'other';

export interface ResearchGroupInfo {
  key: ResearchFieldGroup;
  label: string;
}

export interface ResearchChangeSummary {
  /** Fields whose value changed — the ones that decide things. */
  values: number;
  /** Prose fields the model rewrote. */
  reworded: number;
  /** Value changes per field group. */
  groups: Partial<Record<ResearchFieldGroup, number>>;
}

export interface ResearchFieldChange {
  group: ResearchFieldGroup;
  path: string;
  label: string;
  kind: 'value' | 'text';
  change: 'changed' | 'added' | 'removed';
  old: unknown;
  new: unknown;
  added_items?: unknown[];
  removed_items?: unknown[];
}

export interface ResearchVersion {
  _id: string;
  profile_id: string;
  /** baseline: the research a profile had before history was kept. */
  kind: 'baseline' | 'run';
  first_research: boolean;
  researched_at: string | null;
  recorded_at: string;
  researched_by: string | null;
  by_email: string | null;
  trigger: 'manual' | 'scheduled' | null;
  designation_category: string | null;
  profile_name: string | null;
  changes: ResearchFieldChange[];
  summary: ResearchChangeSummary;
  has_changes: boolean;
}

export interface ResearchHistoryResponse {
  profile_id: string;
  versions: ResearchVersion[];
  groups: ResearchGroupInfo[];
}

export interface ResearchChangesQuery {
  q?: string;
  group?: ResearchFieldGroup;
  changed_only?: boolean;
  sectors?: string[];
  locations?: string[];
  target_asks?: string[];
  designations?: string[];
  page?: number;
  limit?: number;
}

export interface ResearchChangesRow {
  profile_id: string;
  name: string | null;
  designation_category: string | null;
  researched_at: string | null;
  researched_by: string | null;
  trigger: 'manual' | 'scheduled' | null;
  summary: ResearchChangeSummary;
  has_changes: boolean;
  /** How many times the profile has been re-researched. */
  reruns: number;
}

export interface ResearchChangesResponse {
  rows: ResearchChangesRow[];
  total: number;
  page: number;
  pages: number;
  groups: ResearchGroupInfo[];
}

/** What the AI settings tabs call: dashboardService for projects, proposalPromptApi for proposals. */
export type PromptApi = Pick<typeof dashboardService,
  'getPrompt' | 'savePrompt' | 'restorePrompt' | 'patchRule' | 'recheckRule' | 'deleteRule'>;
